/**
 * 公車畫面編號／縣市碼（與 backend/utils/bus-route-display.js 保持同步）
 */

export const TDX_CITY_CANON = {
    TPE: 'TPE',
    NWT: 'NTP',
    NTP: 'NTP',
    TYN: 'TYN',
    TAO: 'TYN',
    TXG: 'TXG',
    TNN: 'TNN',
    KHH: 'KHH',
    KEE: 'KEE',
    HSZ: 'HSZ',
    HSQ: 'HSQ',
    MIA: 'MIA',
    CHA: 'CHA',
    NAN: 'NAN',
    YUL: 'YUL',
    YUN: 'YUL',
    CYQ: 'CYQ',
    CYI: 'CYI',
    PIF: 'PIF',
    ILA: 'ILA',
    HUA: 'HUA',
    TTT: 'TTT',
    PEN: 'PEN',
    KIN: 'KIN',
    LIE: 'LIE'
};

export const TDX_CITY_ZH = {
    TPE: '台北市',
    NWT: '新北市',
    NTP: '新北市',
    TYN: '桃園市',
    TAO: '桃園市',
    TXG: '台中市',
    TNN: '台南市',
    KHH: '高雄市',
    KEE: '基隆市',
    HSZ: '新竹市',
    HSQ: '新竹縣',
    MIA: '苗栗縣',
    CHA: '彰化縣',
    NAN: '南投縣',
    YUL: '雲林縣',
    YUN: '雲林縣',
    CYQ: '嘉義縣',
    CYI: '嘉義市',
    PIF: '屏東縣',
    ILA: '宜蘭縣',
    HUA: '花蓮縣',
    TTT: '台東縣',
    PEN: '澎湖縣',
    KIN: '金門縣',
    LIE: '連江縣'
};

export function normalizeBusCityCode(code) {
    const raw = String(code || '').trim().toUpperCase();
    if (!raw) return '';
    return TDX_CITY_CANON[raw] || '';
}

export function cityLabel(code) {
    const raw = String(code || '').trim().toUpperCase();
    if (!raw) return '';
    return TDX_CITY_ZH[raw] || TDX_CITY_ZH[normalizeBusCityCode(raw)] || '';
}

const SCHEDULE_NOTE_RE = /週|周|每週|每周|行駛|發車|預約|停駛|延駛/;
const PING_JIA_RE = /[平假]\d{3,4}/;
const DASH_HHMM_RE = /^([A-Za-z]\d{1,4})-(?:[01]\d|2[0-3])[0-5]\d$/;
const ROUTE_KEY_LEAK_RE = /^[A-Z]{3}\d{4,}(_D\d+)?$/i;
const FCODE_SCHEDULE_LIST_RE = /^([A-Za-z]\d{1,4})-(?:[01]\d|2[0-3])[0-5]\d(?:\s*[、,，]\s*(?:[01]\d|2[0-3])[0-5]\d)+$/i;
const COMMUTE_HHMM_RE = /^(.+?通勤)\d{3,4}$/;
const CLOCK_PAREN_RE = /^(.+?)[（(]\d{1,2}:\d{2}/;

function looksLikeRouteToken(token) {
    const t = String(token || '').trim();
    if (!t) return false;
    if (/^(幸福巴士|台灣好行|南環幹線|民權幹線|仁愛幹線|和平幹線|復興幹線|民生幹線)$/.test(t)) {
        return false;
    }
    return (
        /^\d+[路區]?$/.test(t) ||
        /^[小紅藍綠橘黃棕]\d+[路區甲乙丙丁]?$/.test(t) ||
        /^[A-Za-z]\d+[A-Za-z]?$/.test(t) ||
        /^JOY\s*H?\d+[A-Za-z]?$/i.test(t) ||
        /^T\d+[A-Za-z]?$/i.test(t)
    );
}

export function isLeakedBusRouteKey(raw) {
    return ROUTE_KEY_LEAK_RE.test(String(raw || '').trim());
}

export function isDirtyBusRouteRef(raw) {
    const s = String(raw || '').trim();
    if (!s) return false;
    if (PING_JIA_RE.test(s)) return true;
    if (DASH_HHMM_RE.test(s)) return true;
    if (FCODE_SCHEDULE_LIST_RE.test(s)) return true;
    if (COMMUTE_HHMM_RE.test(s)) return true;
    if (CLOCK_PAREN_RE.test(s)) return true;
    if (isLeakedBusRouteKey(s)) return true;
    if (/^\d{1,2}:\d{2}\b/.test(s)) return true;
    if (SCHEDULE_NOTE_RE.test(s) && /[（(]/.test(s)) return true;
    return false;
}

export function cleanBusRouteRef(raw) {
    let s = String(raw || '').replace(/\s+/g, ' ').trim();
    if (!s) return '';

    if (isLeakedBusRouteKey(s)) return '';

    s = s.replace(/^\d{1,2}:\d{2}\s+/, '');

    const dash = s.match(DASH_HHMM_RE);
    if (dash) return dash[1].toUpperCase();

    const fList = s.match(FCODE_SCHEDULE_LIST_RE);
    if (fList) return fList[1].toUpperCase();

    const commute = s.match(COMMUTE_HHMM_RE);
    if (commute) return commute[1];

    const clockParen = s.match(CLOCK_PAREN_RE);
    if (clockParen) {
        const head = clockParen[1].trim();
        if (looksLikeRouteToken(head)) return head;
        s = s.replace(/[（(]\d{1,2}:\d{2}[^)）]*[)）]/g, '').trim();
    }

    if (PING_JIA_RE.test(s)) {
        const first = s.split(/\s+/)[0];
        return first.replace(PING_JIA_RE, '').trim() || first;
    }

    s = s.replace(/[（(][^)）]*[)）]/g, (paren) => (SCHEDULE_NOTE_RE.test(paren) ? '' : paren));
    s = s.replace(/\s+/g, ' ').trim();

    if (/[→→]/.test(s)) {
        const first = s.split(/\s+/)[0];
        if (looksLikeRouteToken(first)) return first;
    }

    const spaced = s.match(
        /^(\d+[路區甲乙丙丁]?|[小紅藍綠橘黃棕]\d+[路區甲乙丙丁]?|[A-Za-z]\d+[A-Za-z]?)\s+.+$/
    );
    if (spaced && looksLikeRouteToken(spaced[1])) return spaced[1];

    return s;
}
